import { useApp } from 'contexts/app';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { PageContext } from 'pages/People/PageProvider';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Divider,
    Dropdown,
    EmailMultiInput,
    Form,
    IconButton,
    ResponsiveDialog,
    Stack,
    styled,
    Text,
} from 'shell/components';
import { getUserPreferredFullName, splitArrayByCondition } from 'utils/helper';
import { emailRegex } from 'utils/regex';

const InstructorInvitations = ({ open, onClose }) => {
    const { accountUsers, instructorAccountUsers } = useApp();
    const { message } = useSnackbar();
    const { device } = useViewport();
    const { t } = useTranslation();

    const [inputValue, setInputValue] = useState('');
    const [emails, setEmails] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const { sendInvitations, getMemberActionOptions, handleSelectMemberAction } = useContext(PageContext);

    const hasAlreadyBeenInvited = (email) => {
        return Boolean(accountUsers.find((accountUser) => accountUser.email === email));
    };

    const handleClose = () => {
        if (!isLoading) onClose();
    };

    const handleSubmit = async () => {
        if (!canSubmit || isLoading) return;
        if (inputValue) {
            setEmails((current) => [...current, inputValue]);
            setInputValue('');
        } else {
            const uniqueEmails = [...new Set(emails)];
            const { true: alreadyInvited, false: toBeInvited } = splitArrayByCondition(
                uniqueEmails,
                hasAlreadyBeenInvited
            );
            setIsLoading(true);
            if (toBeInvited.length) {
                await sendInvitations(
                    'instructor',
                    toBeInvited.map((email) => ({ email }))
                );
            }
            setEmails([]);
            setIsLoading(false);
            if (toBeInvited.length && alreadyInvited.length) {
                message.success(
                    [
                        t('message.invite-success', '{{count}} member(s) successfully invited.', {
                            count: toBeInvited.length,
                        }),
                        t('message.existing-members', '{{count}} member(s) already in the course.', {
                            count: alreadyInvited.length,
                        }),
                    ].join(', ')
                );
            }
            if (!toBeInvited.length && alreadyInvited.length) {
                message.error(
                    t('message.existing-members', '{{count}} member(s) already in the course', {
                        count: alreadyInvited.length,
                    })
                );
            }
            if (toBeInvited.length && !alreadyInvited.length) {
                message.success(
                    t('message.invite-success', '{{count}} member(s) successfully invited', {
                        count: toBeInvited.length,
                    })
                );
            }
        }
    };

    const handleClick = (_, targetIndex) => {
        setEmails((current) => current.filter((_, index) => index !== targetIndex));
    };

    const isInputValueValid = emailRegex.test(inputValue);
    const canSubmit =
        isInputValueValid ||
        (Boolean(emails.length) && !Boolean(inputValue) && emails.every((email) => emailRegex.test(email)));

    return (
        <ResponsiveDialog aria-labelledby={'dialog-title'} open={open} onClose={handleClose}>
            <RootStack device={device} divider={<Divider />} spacing={7}>
                <Stack>
                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Text id={'dialog-title'} component={'h1'} variant={'h6'}>
                            {t('people.invite-instructor.title', 'Invite Instructors to the Course')}
                        </Text>
                        <IconButton
                            aria-label={'close'}
                            disabled={isLoading}
                            kind={'exit'}
                            size={'medium'}
                            variant={'square'}
                            onClick={handleClose}
                        />
                    </Stack>
                    <Form onSubmit={handleSubmit}>
                        <FormStack alignItems={'center'} direction={'row'} spacing={3}>
                            <EmailMultiInput
                                aria-label={'enter email'}
                                inputValue={inputValue}
                                value={emails}
                                onClick={handleClick}
                                onChange={setEmails}
                                onChangeInput={(e) => setInputValue(e.target.value)}
                            />
                            <Button disabled={!canSubmit} loading={isLoading} type={'submit'}>
                                {t('people.invite-instructor.send-invite', 'Send Invite')}
                            </Button>
                        </FormStack>
                    </Form>
                </Stack>
                <Stack spacing={2}>
                    <Text variant={'sh2'} weight={'semibold'}>
                        {t('people.invite-instructor.instructors-invited', 'Instructors Invited ({count})', {
                            count: instructorAccountUsers.length,
                        })}
                    </Text>
                    <InvitedStack device={device} divider={<Divider />}>
                        {instructorAccountUsers.map((accountUser, index) => (
                            <InvitedItemStack
                                key={accountUser.owner_account_id}
                                direction={'row'}
                                index={index}
                                justifyContent={'space-between'}
                                spacing={2}
                            >
                                <Text sx={{ mr: 2 }} variant={'p1'}>
                                    {index + 1}
                                </Text>
                                <Stack sx={{ flex: 1 }}>
                                    <Text variant={'p1'}>{getUserPreferredFullName(accountUser)}</Text>
                                    <Text color={'light'} variant={'p2'}>
                                        {accountUser.email}
                                    </Text>
                                </Stack>
                                <Dropdown
                                    id={`invite-instructor-dropdown-${accountUser.owner_account_id}`}
                                    options={getMemberActionOptions(accountUser)}
                                    rootComponent={
                                        <IconButton
                                            aria-label={'more options'}
                                            kind={'moreHorizontal'}
                                            size={'small'}
                                            variant={'square'}
                                        />
                                    }
                                    onSelect={({ key }) => handleSelectMemberAction(key, accountUser)}
                                />
                            </InvitedItemStack>
                        ))}
                    </InvitedStack>
                </Stack>
            </RootStack>
        </ResponsiveDialog>
    );
};

const RootStack = styled(Stack)(({ device }) => {
    return {
        width: device === 'mobile' ? '100%' : 620,
    };
});

const FormStack = styled(Stack)(({ theme }) => {
    return {
        marginTop: theme.spacing(6),
    };
});

const InvitedStack = styled(Stack)(({ device }) => {
    return {
        ...(device !== 'mobile' && {
            maxHeight: 325,
            overflowY: 'auto',
        }),
    };
});

const InvitedItemStack = styled(Stack)(({ theme, index }) => {
    return {
        padding: theme.spacing(3),
        background: !(index % 2) ? theme.palette.ensightful.neutral[100] : theme.palette.ensightful.shades[0],
    };
});

export default InstructorInvitations;
